import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import React from 'react'
import AnimatedSection from 'src/components/AnimatedSection'
import BaseButton from 'src/components/Buttons/BaseButton'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'

type IProps = {
  pillar: PillarPage
  ctaHref: string
}

const RentSpace = ({ pillar, ctaHref }: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const features: { title: string; description: string }[] = t(
    'RENT_SPACE.ITEMS',
    { returnObjects: true }
  )
  const image: { src: string; alt: string } = t('RENT_SPACE.IMAGES', {
    returnObjects: true
  })

  return (
    <AnimatedSection
      variant="slideUp"
      className="w-full bg-white-500 py-12 md:py-16"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* Header */}
        <div className="mb-10 flex flex-col items-center gap-3 text-center">
          <h2 className="max-w-2xl font-display text-2xl font-medium text-ink-700 md:text-3xl">
            {t('RENT_SPACE.TITLE')}
          </h2>
          <p className="text-base font-medium text-blue-800">
            {t('RENT_SPACE.SUBTITLE')}
          </p>
        </div>

        {/* Content */}
        <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
          {/* Image — `object-cover` in a 16:10 frame, NOT `object-contain` in a
              padded box. The old version paired a 3.47:1 collage with a 1.6:1
              container, so contain letterboxed it to ~101px inside a 240px
              frame: the photo rendered as postage stamps floating in a grey
              gradient, with ~139px of dead space. The gradient + double
              padding existed only to cushion that gap. Sources are now
              per-pillar 1200×750 (16:10) product photos, so the frame fills
              edge to edge. Keep the container aspect and the source aspect in
              step if either changes. */}
          <div className="flex justify-center">
            <div className="relative aspect-[16/10] w-full max-w-md overflow-hidden rounded-2xl ring-1 ring-ink-100/15">
              <Image
                src={image.src}
                alt={image.alt}
                fill
                sizes="(max-width: 900px) 90vw, 448px"
                className="object-cover"
              />
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-col gap-4">
            {features.map(({ title, description }, index) => (
              <div key={index} className="rounded-xl bg-cream-300 p-4">
                <h3 className="mb-1 text-base font-semibold text-ink-700">
                  {title}
                </h3>
                <p className="text-sm text-ink-200">{description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 flex justify-center">
          <BaseButton
            fontSize={14}
            size="medium"
            color="primary"
            variant="contained"
            rounded
            href={ctaHref}
          >
            {t('RENT_SPACE.BUTTON_PHOLDER')}
          </BaseButton>
        </div>
      </div>
    </AnimatedSection>
  )
}

export default React.memo(RentSpace)
